import { Component } from '@theme/component';

/**
 * Shipping Calculator Component
 * Provides autocomplete country selection and shipping calculation
 */
export default class ShippingCalculatorComponent extends Component {
  /** @type {Array<{id: string, name: string}>} */
  #countries = [];
  
  /** @type {Array<{id: string, name: string}>} */
  #filteredCountries = [];
  
  /** @type {number} */
  #selectedIndex = -1;
  
  /** @type {string | null} */
  #selectedCountryId = null;
  
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeCountries();
    this.#setupEventListeners();
    this.#autoFetchInitialShippingOptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #initializeCountries() {
    try {
      const countryDataElement = this.refs.countryData;
      if (countryDataElement) {
        this.#countries = JSON.parse(countryDataElement.textContent);
        this.#filteredCountries = [...this.#countries];
      }
    } catch (error) {
      console.error('Failed to parse country data:', error);
    }
  }

  #setupEventListeners() {
    this.refs.countryInput?.addEventListener('input', this.#handleInput.bind(this));
    this.refs.countryInput?.addEventListener('focus', this.#handleFocus.bind(this));
    this.refs.countryInput?.addEventListener('blur', this.#handleBlur.bind(this));
    this.refs.countryInput?.addEventListener('keydown', this.#handleKeydown.bind(this));
    this.refs.form?.addEventListener('submit', this.#handleSubmit.bind(this));

    document.addEventListener('click', this.#handleDocumentClick.bind(this));
  }

  #handleInput(event) {
    const query = event.target.value.toLowerCase().trim();
    
    if (!query) {
      this.#filteredCountries = [...this.#countries];
    } else {
      this.#filteredCountries = this.#countries.filter(country =>
        country.name.toLowerCase().includes(query)
      );
    }
    
    this.#selectedIndex = -1;
    this.#updateDropdown();
    this.#showDropdown();
    this.#updateSubmitButton();
  }

  #handleFocus() {
    this.#showDropdown();
  }

  #handleBlur() {
    setTimeout(() => {
      this.#hideDropdown();
    }, 150);
  }

  #handleKeydown(event) {
    if (!this.#isDropdownVisible()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#selectedIndex = Math.min(this.#selectedIndex + 1, this.#filteredCountries.length - 1);
        this.#updateSelection();
        break;
      
      case 'ArrowUp':
        event.preventDefault();
        this.#selectedIndex = Math.max(this.#selectedIndex - 1, 0);
        this.#updateSelection();
        break;
      
      case 'Enter':
        event.preventDefault();
        if (this.#selectedIndex >= 0) {
          this.#selectCountry(this.#filteredCountries[this.#selectedIndex]);
        }
        break;
      
      case 'Escape':
        this.#hideDropdown();
        break;
    }
  }

  async #handleSubmit(event) {
    event.preventDefault();
    
    const countryId = this.refs.countryIdInput?.value;
    if (!countryId) {
      this.#showError('Please select a country');
      return;
    }

    await this.#fetchShippingOptions(countryId);
  }

  #handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.#hideDropdown();
    }
  }

  #updateDropdown() {
    const optionsList = this.refs.optionsList;
    if (!optionsList) return;

    optionsList.innerHTML = '';

    if (this.#filteredCountries.length === 0) {
      optionsList.innerHTML = '<li class="shipping-calculator__option">No results found</li>';
      return;
    }

    this.#filteredCountries.forEach((country, index) => {
      const li = document.createElement('li');
      li.className = 'shipping-calculator__option';
      li.dataset.countryId = country.id;
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      
      if (country.id === this.#selectedCountryId) {
        li.setAttribute('aria-current', 'true');
      }
      
      const checkmark = document.createElement('span');
      checkmark.className = 'svg-wrapper icon-checkmark';
      checkmark.innerHTML = this.#getCheckmarkSVG();
      
      const textSpan = document.createElement('span');
      textSpan.className = 'shipping-calculator__option-text';
      textSpan.textContent = country.name;
      
      li.appendChild(checkmark);
      li.appendChild(textSpan);
      
      li.addEventListener('click', () => this.#selectCountry(country));
      
      optionsList.appendChild(li);
    });
  }

  #getCheckmarkSVG() {
    return `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark">
        <path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    `;
  }

  #updateSelection() {
    const options = this.refs.optionsList?.querySelectorAll('.shipping-calculator__option');
    if (!options) return;

    options.forEach((option, index) => {
      option.classList.toggle('shipping-calculator__option--selected', index === this.#selectedIndex);
    });

    if (this.#selectedIndex >= 0 && options[this.#selectedIndex]) {
      options[this.#selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  #selectCountry(country) {
    this.refs.countryInput.value = country.name;
    this.refs.countryIdInput.value = country.id;
    this.#selectedCountryId = country.id;
    
    this.#updateDropdown();
    this.#hideDropdown();
    this.#updateSubmitButton();
    this.#clearError();
  }

  #showDropdown() {
    if (this.refs.dropdown) {
      this.#updateDropdown();
      this.refs.dropdown.hidden = false;
      this.refs.countryInput?.setAttribute('aria-expanded', 'true');
    }
  }

  #hideDropdown() {
    if (this.refs.dropdown) {
      this.refs.dropdown.hidden = true;
      this.refs.countryInput?.setAttribute('aria-expanded', 'false');
    }
  }

  #isDropdownVisible() {
    return this.refs.dropdown && !this.refs.dropdown.hidden;
  }

  #updateSubmitButton() {
    const hasSelection = this.refs.countryIdInput?.value && this.refs.countryIdInput.value !== '';
    if (this.refs.submitButton) {
      this.refs.submitButton.disabled = !hasSelection;
    }
  }

  async #autoFetchInitialShippingOptions() {
    setTimeout(async () => {
      try {
        const initialCountryId = this.dataset.initialCountryId || '13';
        
        await this.#fetchShippingOptions(initialCountryId);
        
        this.#updateSubmitButton();
        
      } catch (error) {
        console.error('Auto-fetch of initial shipping options failed:', error);
      }
    }, 100);
  }

  async #fetchShippingOptions(countryId) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const apiUrl = this.refs.countryIdInput?.dataset.apiUrl?.replace('{{countryId}}', countryId);
    if (!apiUrl) {
      this.#showError('API URL not configured');
      return;
    }

    this.#setLoadingState(true);
    this.#clearError();

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: this.#abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.#showResults(data);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Request was cancelled
      }
      
      console.error('Shipping calculation failed:', error);
      this.#showError('Failed to calculate shipping. Please try again.');
    } finally {
      this.#setLoadingState(false);
      this.#abortController = null;
    }
  }

  #setLoadingState(isLoading) {
    if (this.refs.submitButton) {
      this.refs.submitButton.classList.toggle('loading', isLoading);
    }
  }

  #showResults(data) {
    if (this.refs.resultsContainer && this.refs.resultsContent) {
      this.refs.resultsContent.innerHTML = this.#formatResults(data);
      this.refs.resultsContainer.hidden = false;
    }
  }

  #formatResults(data) {
    console.log('Shipping data received:', data);
    
    let options = [];
    
    if (data && data.Options && Array.isArray(data.Options)) {
      options = data.Options;
    } else if (Array.isArray(data)) {
      options = data;
    } else if (data && typeof data === 'object') {
      options = [data];
    }
    
    if (options.length === 0) {
      return '<p class="shipping-calculator__no-options">No shipping options available for this country.</p>';
    }

    const processedOptions = [];
    
    options.forEach(option => {
      if (option.FreeThreshold) {
        processedOptions.push({
          ...option,
          Name: `${option.Name}`,
          Price: 'FREE',
          IsFreeVersion: true,
          FreeCondition: `Free over ${option.FreeThreshold}`
        });
        
        processedOptions.push({
          ...option,
          IsFreeVersion: false,
          Selected: false
        });
      } else {
        processedOptions.push({
          ...option,
          IsFreeVersion: false
        });
      }
    });

    return `
      <div class="shipping-calculator__table-wrapper">
        <table class="shipping-calculator__table">
          <thead>
            <tr class="shipping-calculator__table-header">
              <th class="shipping-calculator__table-th">Type</th>
              <th class="shipping-calculator__table-th">Delivery time</th>
              <th class="shipping-calculator__table-th">Cost</th>
            </tr>
          </thead>
          <tbody>
            ${processedOptions.map(option => `
              <tr class="shipping-calculator__table-row ${option.Selected ? 'shipping-calculator__table-row--selected' : ''} ${option.IsFreeVersion ? 'shipping-calculator__table-row--free' : ''}">
                <td class="shipping-calculator__table-td shipping-calculator__table-td--name">
                  ${this.#formatShippingName(option)}
                </td>
                <td class="shipping-calculator__table-td shipping-calculator__table-td--delivery">
                  ${this.#formatDeliveryTime(option)}
                </td>
                <td class="shipping-calculator__table-td shipping-calculator__table-td--price">
                  ${this.#formatPrice(option)}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  #formatShippingName(option) {
    let name = option.Name || option.name || option.service_name || 'Standard Shipping';
    
    if (option.IsFreeVersion && option.FreeCondition) {
      name += `<br><small class="shipping-calculator__condition">${option.FreeCondition}</small>`;
    }
    
    return name;
  }

  #formatDeliveryTime(option) {
    if (option.MinDays && option.MaxDays) {
      if (option.MinDays === option.MaxDays) {
        return `${option.MinDays} day${option.MinDays > 1 ? 's' : ''}`;
      }
      return `${option.MinDays}-${option.MaxDays} business days`;
    } else if (option.delivery_time || option.estimated_delivery || option.timeframe) {
      return option.delivery_time || option.estimated_delivery || option.timeframe;
    } else if (option.MinDays) {
      return `${option.MinDays}+ business days`;
    } else if (option.MaxDays) {
      return `Up to ${option.MaxDays} business days`;
    }
    return 'Standard delivery';
  }

  #formatPrice(option) {
    let price = option.Price || option.price || option.cost || option.rate || 'Free';
    
    if (option.IsFreeVersion) {
      return 'FREE';
    }
    
    return price;
  }

  #showError(message) {
    if (this.refs.errorContainer && this.refs.errorMessage) {
      this.refs.errorMessage.textContent = message;
      this.refs.errorContainer.hidden = false;
    }
  }

  #clearError() {
    if (this.refs.errorContainer) {
      this.refs.errorContainer.hidden = true;
    }
  }
}

customElements.define('shipping-calculator-component', ShippingCalculatorComponent); 